import { Route, Routes, Navigate } from "react-router-dom";

import "./tokens.css";
import "./App.css";
import "./SubmitJobs/ImportForm.css";
import { Fade, useMediaQuery } from "@mui/material";
import React, { Suspense } from "react";
import { CustomToolbar, CustomTitle, DrawerNavigation, NavLink, SIDEBAR_COLLAPSED_WIDTH } from "./Toolbar";
import { LoginForm, Logout, Register, PasswordReset, RegistrationContactNotice } from "./Authentication";
import { browserTokenStore } from "@gui/infrastructure/pass-through/storage";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import * as Sentry from "@sentry/react";
import { LoadingComponent } from "@gui/shared";
import HomePage from "./Home/Home";
import { DEFAULT_PAGE } from "@domain/constants";
import { User } from "@domain/user";
import { useCurrentUser } from "./useCurrentUser";
import { AppProviders, theme } from "./providers/AppProviders";

// Lazy-loaded routes — split into separate chunks
const JobsDashboardWithLocalizationProvider = React.lazy(() => import("./JobsDashboard/JobsDashboard"));
const JobDetailPage = React.lazy(() => import("./JobsDashboard/JobDetail").then((m) => ({ default: m.JobDetailPage })));
const JobSubmissionForm = React.lazy(() => import("./SubmitJobs/ImportForm"));
const MarkJobReadyPage = React.lazy(() =>
	import("./MarkJobsReady/MarkJobReadyPage").then((m) => ({ default: m.MarkJobReadyPage })),
);
const Profile = React.lazy(() => import("./Profile").then((m) => ({ default: m.Profile })));

// Admin pages — rarely visited, biggest code-splitting win
const AgrianIssues = React.lazy(() => import("./Admin/AgrianIssues").then((m) => ({ default: m.AgrianIssues })));
const DataSendbackIssues = React.lazy(() =>
	import("./Admin/DataSendbackIssues").then((m) => ({ default: m.DataSendbackIssues })),
);
const ExeMissionValidation = React.lazy(() =>
	import("./Admin/ExeMissionValidation").then((m) => ({ default: m.ExeMissionValidation })),
);
const GeoFileConverter = React.lazy(() =>
	import("./Admin/GeoFileConverter").then((m) => ({ default: m.GeoFileConverter })),
);
const ImportSettings = React.lazy(() => import("./Admin/ImportSettings").then((m) => ({ default: m.ImportSettings })));
const MissionControlLogs = React.lazy(() =>
	import("./Admin/MissionControlLogs").then((m) => ({ default: m.MissionControlLogs })),
);
const MissionControlPasswordsGenerator = React.lazy(() =>
	import("./Admin/MissionControlPasswordsGenerator").then((m) => ({
		default: m.MissionControlPasswordsGenerator,
	})),
);
const PasswordGenerator = React.lazy(() =>
	import("./Admin/PasswordGenerator").then((m) => ({ default: m.PasswordGenerator })),
);
const SortShapefiles = React.lazy(() => import("./Admin/SortShapefiles").then((m) => ({ default: m.SortShapefiles })));
const Robots = React.lazy(() => import("./Admin/Robots").then((m) => ({ default: m.Robots })));
const JobDiagnosticsPage = React.lazy(() =>
	import("./Admin/JobDiagnostics").then((m) => ({ default: m.JobDiagnosticsPage })),
);
const DealCreator = React.lazy(() => import("./Admin/DealCreator").then((m) => ({ default: m.DealCreator })));
const DealMatcher = React.lazy(() => import("./Admin/DealMatcher").then((m) => ({ default: m.DealMatcher })));
const BranchMatcher = React.lazy(() => import("./Admin/BranchMatcher").then((m) => ({ default: m.BranchMatcher })));

interface ProtectedRouteProps {
	redirectPath?: string;
}
const ProtectedRoute = ({ children, redirectPath }: React.PropsWithChildren<ProtectedRouteProps>) => {
	const jwtToken = browserTokenStore.getJwt();
	if (!jwtToken) {
		return <Navigate to={redirectPath ?? "/login"} replace />;
	}

	return children;
};

function App() {
	// User identity + load state managed by useCurrentUser. The hook owns the
	// auth-failure interceptor wiring, the user fetch, and Sentry user reporting.
	const { currentUser, loading, setCurrentUser, setLoading } = useCurrentUser();
	const [currentLink, setCurrentLink] = React.useState<NavLink>({ to: "/login", text: "Login" });
	const [mobileOpen, setMobileOpen] = React.useState(false);
	const isMobile = useMediaQuery(theme.breakpoints.down("md"));

	const handleLogin = (user: User) => {
		setCurrentUser(user);
	};

	return loading ? (
		<div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100vh" }}>
			<LoadingComponent />
		</div>
	) : (
		<Fade in={!loading}>
			<div>
				<AppProviders currentUser={currentUser}>
					<CustomTitle setCurrentLink={setCurrentLink} />
					<div
						style={{
							height: "100vh",
							overflow: "hidden",
							display: "flex",
							flexDirection: "column",
						}}
					>
						<CustomToolbar title={currentLink.text} onMenuClick={() => setMobileOpen(true)} />
						<DrawerNavigation
							onNavigate={(link: NavLink) => {
								setCurrentLink(link);
							}}
							mobileOpen={mobileOpen}
							onMobileClose={() => setMobileOpen(false)}
						/>

						{/* Route container flex-fills the remaining viewport height after the toolbar. The
						    previous `calc(100vh - 64px)` assumed a single-row 64px Toolbar, but on narrow
						    mobile widths the toolbar wraps to two rows (~96px), which clipped the bottom
						    32px of every page. Using flex: 1 + minHeight: 0 adapts to whatever the
						    toolbar's natural height is. */}
						<div
							style={{
								marginLeft: isMobile ? 0 : SIDEBAR_COLLAPSED_WIDTH,
								flex: 1,
								minHeight: 0,
								overflowY: "auto",
							}}
						>
							<Suspense fallback={<LoadingComponent />}>
								<Routes>
									<Route path="/home" element={<HomePage setLoggedIn={handleLogin} />} />
									<Route
										path="/login"
										element={
											currentUser ? <Navigate to={DEFAULT_PAGE} replace /> : <LoginForm setLoggedIn={handleLogin} />
										}
									/>
									<Route path="/register" element={<Register setLoggedIn={handleLogin} />} />
									<Route
										path="/"
										element={currentUser ? <Navigate to={DEFAULT_PAGE} /> : <LoginForm setLoggedIn={handleLogin} />}
									/>
									<Route path="/forgot-password" element={<PasswordReset setLoggedIn={handleLogin} />} />
									<Route path="/reset-password" element={<PasswordReset setLoggedIn={handleLogin} />} />
									<Route path="/jobs">
										<Route
											index
											path="dashboard"
											element={
												<ProtectedRoute>
													<JobsDashboardWithLocalizationProvider setLoading={setLoading} />
												</ProtectedRoute>
											}
										/>
										<Route
											path="submit"
											element={
												<ProtectedRoute>
													<JobSubmissionForm />
												</ProtectedRoute>
											}
										/>
										<Route
											path="ready"
											element={
												<ProtectedRoute>
													<MarkJobReadyPage setLoading={setLoading} />
												</ProtectedRoute>
											}
										/>
										<Route
											path=":jobId"
											element={
												<ProtectedRoute>
													<JobDetailPage />
												</ProtectedRoute>
											}
										/>
									</Route>
									<Route
										path="/profile"
										element={
											<ProtectedRoute>
												<Profile />
											</ProtectedRoute>
										}
									/>
									<Route path="/logout" element={<Logout />} />
									<Route path="/registration-notice" element={<RegistrationContactNotice />} />
									<Route path="/admin">
										<Route
											index
											path="sortshp"
											element={
												<ProtectedRoute>
													<SortShapefiles />
												</ProtectedRoute>
											}
										/>
										<Route
											path="import"
											element={
												<ProtectedRoute>
													<ImportSettings />
												</ProtectedRoute>
											}
										/>
										<Route
											path="geofile-converter"
											element={
												<ProtectedRoute>
													<GeoFileConverter />
												</ProtectedRoute>
											}
										/>
										<Route
											path="mission-control-logs"
											element={
												<ProtectedRoute>
													<MissionControlLogs />
												</ProtectedRoute>
											}
										/>
										<Route
											path="mission-control-passwords-generator"
											element={
												<ProtectedRoute>
													<MissionControlPasswordsGenerator />
												</ProtectedRoute>
											}
										/>
										<Route
											path="password-generator"
											element={
												<ProtectedRoute>
													<PasswordGenerator />
												</ProtectedRoute>
											}
										/>
										<Route
											path="agrian-issues"
											element={
												<ProtectedRoute>
													<AgrianIssues />
												</ProtectedRoute>
											}
										/>
										<Route
											path="data-sendback-issues"
											element={
												<ProtectedRoute>
													<DataSendbackIssues />
												</ProtectedRoute>
											}
										/>
										<Route
											path="exe-mission-validation"
											element={
												<ProtectedRoute>
													<ExeMissionValidation />
												</ProtectedRoute>
											}
										/>
										<Route
											path="exe-mission-validation/:jobId"
											element={
												<ProtectedRoute>
													<ExeMissionValidation />
												</ProtectedRoute>
											}
										/>
										<Route
											path="robots"
											element={
												<ProtectedRoute>
													<Robots />
												</ProtectedRoute>
											}
										/>
										<Route
											path="robots/:robotId"
											element={
												<ProtectedRoute>
													<Robots />
												</ProtectedRoute>
											}
										/>
										<Route
											path="robots/:robotId/:shiftId"
											element={
												<ProtectedRoute>
													<Robots />
												</ProtectedRoute>
											}
										/>
										<Route
											path="job-diagnostics"
											element={
												<ProtectedRoute>
													<JobDiagnosticsPage />
												</ProtectedRoute>
											}
										/>
										<Route
											path="job-diagnostics/:jobId"
											element={
												<ProtectedRoute>
													<JobDiagnosticsPage />
												</ProtectedRoute>
											}
										/>
										<Route
											path="deal-matcher"
											element={
												<ProtectedRoute>
													<DealMatcher />
												</ProtectedRoute>
											}
										/>
										<Route
											path="deal-creator"
											element={
												<ProtectedRoute>
													<DealCreator />
												</ProtectedRoute>
											}
										/>
										<Route
											path="branch-matcher"
											element={
												<ProtectedRoute>
													<BranchMatcher />
												</ProtectedRoute>
											}
										/>
									</Route>
								</Routes>
							</Suspense>
						</div>
					</div>
				</AppProviders>
				<ToastContainer position="bottom-left" />
			</div>
		</Fade>
	);
}

export default Sentry.withProfiler(App);
